import React from "react";
import { useNavigate } from "react-router-dom";
import { Gift, Tag, ArrowRight, Facebook, Instagram, Youtube, MessageCircle, Music2, Share2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useApp } from "../context/AppContext";

export default function Offers() {
  const nav = useNavigate();
  const { offers, settings } = useApp();
  const activeOffers = offers.filter((o) => o.active !== false);

  const socials = [
    { key: "facebook", label: "Facebook", icon: Facebook, url: settings.facebook },
    { key: "instagram", label: "Instagram", icon: Instagram, url: settings.instagram },
    { key: "tiktok", label: "TikTok", icon: Music2, url: settings.tiktok },
    { key: "youtube", label: "YouTube", icon: Youtube, url: settings.youtube },
  ];
  const activeSocials = socials.filter((s) => s.url);

  return (
    <div>
      <section className="relative grid-bg border-b border-black/10 overflow-hidden">
        <div className="absolute -top-24 right-1/4 w-96 h-96 rounded-full bg-orange-600/15 blur-[120px]" />
        <div className="max-w-7xl mx-auto px-5 py-20 relative animate-fade-up">
          <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">Offers & Community</span>
          <h1 className="font-display font-bold text-4xl md:text-5xl mt-3">Special offers & our socials</h1>
          <p className="text-neutral-600 mt-4 max-w-2xl text-lg leading-relaxed">Grab the latest deals on your shipments and follow us to stay updated with new routes, promotions and community news.</p>
        </div>
      </section>

      {/* Offers */}
      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="flex items-center gap-2 mb-6"><Gift className="w-5 h-5 text-orange-500" /><h2 className="font-display font-bold text-2xl">Current special offers</h2></div>
        {activeOffers.length ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeOffers.map((o) => (
              <div key={o.id} className="glass card-hover rounded-2xl overflow-hidden">
                {o.banner ? <img src={o.banner} alt={o.title} className="w-full h-40 object-cover" />
                  : <div className="h-40 orange-gradient grid place-items-center"><Gift className="w-12 h-12 text-white/90" /></div>}
                <div className="p-6">
                  {o.tag && <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider orange-gradient text-white px-2.5 py-1 rounded-full"><Tag className="w-3 h-3" /> {o.tag}</span>}
                  <h3 className="font-display font-semibold text-lg mt-3">{o.title}</h3>
                  <p className="text-neutral-600 text-sm mt-2 leading-relaxed">{o.desc}</p>
                  <a href={`https://wa.me/${settings.whatsapp}?text=${encodeURIComponent("Hi! I'd like to claim the offer: " + o.title)}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-orange-500 hover:text-orange-400 mt-4 font-medium"><MessageCircle className="w-4 h-4" /> Claim on WhatsApp</a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="glass rounded-2xl p-12 text-center"><Gift className="w-12 h-12 text-neutral-300 mx-auto mb-4" /><p className="text-neutral-600">No offers right now — check back soon!</p></div>
        )}
      </section>

      {/* Social media */}
      <section className="max-w-7xl mx-auto px-5 pb-20">
        <div className="flex items-center gap-2 mb-6"><Share2 className="w-5 h-5 text-orange-500" /><h2 className="font-display font-bold text-2xl">Follow us</h2></div>
        {activeSocials.length ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {activeSocials.map((s) => (
              <a key={s.key} href={s.url} target="_blank" rel="noreferrer" className="glass card-hover rounded-2xl p-7 text-center">
                <div className="w-14 h-14 rounded-2xl orange-gradient grid place-items-center mx-auto"><s.icon className="w-7 h-7 text-white" /></div>
                <p className="font-display font-semibold mt-4">{s.label}</p>
                <p className="text-xs text-neutral-500 mt-1 flex items-center justify-center gap-1">Visit page <ArrowRight className="w-3 h-3" /></p>
              </a>
            ))}
          </div>
        ) : (
          <div className="glass rounded-2xl p-10 text-center">
            <Share2 className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-600">Our social pages are coming soon. In the meantime, reach us on WhatsApp!</p>
            <a href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noreferrer">
              <Button className="orange-gradient text-white border-0 mt-5 gap-2 hover:opacity-90"><MessageCircle className="w-4 h-4" /> Chat on WhatsApp</Button>
            </a>
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-5 pb-24">
        <div className="relative rounded-3xl overflow-hidden orange-gradient p-10 md:p-14 orange-glow">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="relative max-w-2xl">
            <h2 className="font-display font-bold text-3xl text-white">Ready to save on your next shipment?</h2>
            <p className="text-white/90 mt-3 text-lg">Start a parcel now and mention any offer on WhatsApp to apply your discount.</p>
            <Button className="bg-white text-orange-600 hover:bg-white/90 font-semibold h-12 px-6 gap-2 mt-6" onClick={() => nav("/send")}>Send a Parcel <ArrowRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
